"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { CreateDropWaveWrapper } from "@/components/waves/CreateDropWaveWrapper";
import { WaveDropsAllWithoutProvider } from "@/components/waves/drops/wave-drops-all";
import { WaveGallery } from "@/components/waves/gallery";
import MobileMemesArtSubmissionBtn from "@/components/waves/memes/submission/MobileMemesArtSubmissionBtn";
import PrivilegedDropCreator, { DropMode } from "@/components/waves/PrivilegedDropCreator";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { UnreadDividerProvider, useUnreadDivider } from "@/contexts/wave/UnreadDividerContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getHomeRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useWave } from "@/hooks/useWave";
import type { WaveViewMode } from "@/hooks/useWaveViewMode";
import { selectEditingDropId } from "@/store/editSlice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { ActiveDropAction } from "@/types/dropInteractionTypes";
import { commonApiPostWithoutBodyAndResponse } from "@/services/api/common-api";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLayout } from "./layout/LayoutContext";
interface InitialDropState {
  readonly waveId: string;
  readonly serialNo: number;
  readonly dividerSerialNo: number | null;
}
interface MyStreamWaveChatProps {
  readonly wave: ApiWave;
  readonly firstUnreadSerialNo: number | null;
  readonly viewMode: WaveViewMode;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
interface WaveChatLeaveHandlerProps {
  readonly enabled: boolean;
  readonly waveId: string;
}
const WaveChatLeaveHandler: React.FC<WaveChatLeaveHandlerProps> = t0 => {
  const $ = _c(7);
  const {
    enabled,
    waveId
  } = t0;
  const {
    setUnreadDividerSerialNo
  } = useUnreadDivider();
  const {
    removeWaveDeliveredNotifications
  } = useNotificationsContext();
  const {
    invalidateNotifications
  } = useContext(ReactQueryWrapperContext);
  let t1;
  let t2;
  if ($[0] !== enabled || $[1] !== invalidateNotifications || $[2] !== removeWaveDeliveredNotifications || $[3] !== setUnreadDividerSerialNo || $[4] !== waveId) {
    t1 = () => {
      if (!enabled) {
        return;
      }
      return () => {
        setUnreadDividerSerialNo(null);
        (async () => {
          ;
          try {
            await Promise.resolve(removeWaveDeliveredNotifications(waveId));
          } catch (t3) {
            const error = t3;
            console.error("Failed to remove wave delivered notifications:", error);
          }
          ;
          try {
            await commonApiPostWithoutBodyAndResponse({
              endpoint: `notifications/wave/${waveId}/read`
            });
            invalidateNotifications();
          } catch (t4) {
            const error_0 = t4;
            console.error("Failed to mark feed as read:", error_0);
          }
        })();
      };
    };
    t2 = [enabled, waveId, setUnreadDividerSerialNo, removeWaveDeliveredNotifications, invalidateNotifications];
    $[0] = enabled;
    $[1] = invalidateNotifications;
    $[2] = removeWaveDeliveredNotifications;
    $[3] = setUnreadDividerSerialNo;
    $[4] = waveId;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  return null;
};
const MyStreamWaveChat: React.FC<MyStreamWaveChatProps> = t0 => {
  const $ = _c(71);
  const {
    wave,
    firstUnreadSerialNo,
    viewMode,
    onDropClick
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const containerRef = useRef(null);
  const {
    connectedProfile
  } = useAuth();
  const {
    isMemesWave,
    isCurationWave,
    isQuorumWave
  } = useWave(wave);
  const t1 = wave.participation.submission_strategy ?? null;
  let t2;
  if ($[0] !== isCurationWave || $[1] !== isMemesWave || $[2] !== isQuorumWave || $[3] !== t1) {
    t2 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      isQuorumWave,
      submissionStrategy: t1
    });
    $[0] = isCurationWave;
    $[1] = isMemesWave;
    $[2] = isQuorumWave;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const submissionExperience = t2;
  const editingDropId = useSelector(selectEditingDropId);
  const {
    isApp
  } = useDeviceInfo();
  let t3;
  if ($[5] !== wave.id) {
    t3 = {
      waveId: wave.id,
      activeDrop: null
    };
    $[5] = wave.id;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const [activeDropState, setActiveDropState] = useState(t3);
  const activeDrop = activeDropState.waveId === wave.id ? activeDropState.activeDrop : null;
  let t4;
  if ($[7] !== wave.id) {
    t4 = nextActiveDrop => {
      setActiveDropState({
        waveId: wave.id,
        activeDrop: nextActiveDrop
      });
    };
    $[7] = wave.id;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const setActiveDropForWave = t4;
  let t5;
  if ($[9] !== firstUnreadSerialNo || $[10] !== searchParams || $[11] !== wave.id) {
    bb0: {
      const dropParam = searchParams.get("serialNo");
      if (!dropParam) {
        t5 = null;
        break bb0;
      }
      const parsed = Number.parseInt(dropParam, 10);
      if (!Number.isFinite(parsed)) {
        t5 = null;
        break bb0;
      }
      let t6;
      if ($[13] !== firstUnreadSerialNo || $[14] !== searchParams) {
        const dividerParam = searchParams.get("divider");
        const dividerParsed = dividerParam ? Number.parseInt(dividerParam, 10) : null;
        t6 = dividerParsed !== null && Number.isFinite(dividerParsed) ? dividerParsed : firstUnreadSerialNo;
        $[13] = firstUnreadSerialNo;
        $[14] = searchParams;
        $[15] = t6;
      } else {
        t6 = $[15];
      }
      const dividerSerialNo = t6;
      let t7;
      if ($[16] !== dividerSerialNo || $[17] !== parsed || $[18] !== wave.id) {
        t7 = {
          waveId: wave.id,
          serialNo: parsed,
          dividerSerialNo
        };
        $[16] = dividerSerialNo;
        $[17] = parsed;
        $[18] = wave.id;
        $[19] = t7;
      } else {
        t7 = $[19];
      }
      t5 = t7;
    }
    $[9] = firstUnreadSerialNo;
    $[10] = searchParams;
    $[11] = wave.id;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const initialDropState = t5;
  let t6;
  if ($[20] !== firstUnreadSerialNo || $[21] !== initialDropState || $[22] !== wave.id) {
    t6 = () => {
      if (initialDropState?.waveId === wave.id && initialDropState.dividerSerialNo !== null) {
        return {
          waveId: wave.id,
          serialNo: initialDropState.dividerSerialNo
        };
      }
      return {
        waveId: wave.id,
        serialNo: firstUnreadSerialNo
      };
    };
    $[20] = firstUnreadSerialNo;
    $[21] = initialDropState;
    $[22] = wave.id;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const [capturedDividerState] = useState(t6);
  const scrollTarget = initialDropState?.waveId === wave.id ? initialDropState.serialNo : null;
  let dividerTarget = firstUnreadSerialNo;
  if (initialDropState?.waveId === wave.id) {
    dividerTarget = initialDropState.dividerSerialNo;
  } else {
    if (capturedDividerState.waveId === wave.id) {
      dividerTarget = capturedDividerState.serialNo;
    }
  }
  let t7;
  let t8;
  if ($[24] !== initialDropState || $[25] !== pathname || $[26] !== router || $[27] !== searchParams || $[28] !== viewMode) {
    t7 = () => {
      if (!initialDropState || viewMode === "gallery") {
        return;
      }
      const params = new URLSearchParams(searchParams.toString() || "");
      if (!params.has("serialNo") && !params.has("divider")) {
        return;
      }
      params.delete("serialNo");
      params.delete("divider");
      const href = params.toString() ? `${pathname}?${params.toString()}` : pathname || getHomeRoute();
      router.replace(href, {
        scroll: false
      });
    };
    t8 = [initialDropState, pathname, router, searchParams, viewMode];
    $[24] = initialDropState;
    $[25] = pathname;
    $[26] = router;
    $[27] = searchParams;
    $[28] = viewMode;
    $[29] = t7;
    $[30] = t8;
  } else {
    t7 = $[29];
    t8 = $[30];
  }
  useEffect(t7, t8);
  const {
    waveViewStyle
  } = useLayout();
  let t9;
  if ($[31] !== setActiveDropForWave) {
    const onReply = (drop, partId) => {
      setActiveDropForWave({
        action: ActiveDropAction.REPLY,
        drop,
        partId
      });
    };
    t9 = t10 => {
      const {
        drop: drop_0,
        partId: partId_0
      } = t10;
      onReply(drop_0, partId_0);
    };
    $[31] = setActiveDropForWave;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const handleReply = t9;
  let t10;
  if ($[33] !== setActiveDropForWave) {
    t10 = () => {
      setActiveDropForWave(null);
    };
    $[33] = setActiveDropForWave;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const onCancelReplyQuote = t10;
  if (viewMode === "gallery") {
    let t11;
    if ($[35] !== onDropClick || $[36] !== wave) {
      t11 = <WaveGallery wave={wave} onDropClick={onDropClick} />;
      $[35] = onDropClick;
      $[36] = wave;
      $[37] = t11;
    } else {
      t11 = $[37];
    }
    let t12;
    if ($[38] !== t11 || $[39] !== waveViewStyle) {
      t12 = <div ref={containerRef} className="tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden" style={waveViewStyle}>{t11}</div>;
      $[38] = t11;
      $[39] = waveViewStyle;
      $[40] = t12;
    } else {
      t12 = $[40];
    }
    return t12;
  }
  const t11 = dividerTarget ?? null;
  const t12 = `unread-divider-${wave.id}`;
  const t13 = Boolean(connectedProfile?.handle);
  let t14;
  if ($[41] !== t13 || $[42] !== wave.id) {
    t14 = <WaveChatLeaveHandler enabled={t13} waveId={wave.id} />;
    $[41] = t13;
    $[42] = wave.id;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== activeDrop || $[45] !== dividerTarget || $[46] !== handleReply || $[47] !== scrollTarget || $[48] !== wave.id || $[49] !== wave.metrics.muted || $[50] !== wave.metrics.your_unread_drops_count) {
    t15 = <WaveDropsAllWithoutProvider key={wave.id} waveId={wave.id} onReply={handleReply} activeDrop={activeDrop} initialDrop={scrollTarget} dividerSerialNo={dividerTarget} unreadCount={wave.metrics.your_unread_drops_count} dropId={null} isMuted={wave.metrics.muted} />;
    $[44] = activeDrop;
    $[45] = dividerTarget;
    $[46] = handleReply;
    $[47] = scrollTarget;
    $[48] = wave.id;
    $[49] = wave.metrics.muted;
    $[50] = wave.metrics.your_unread_drops_count;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  let t16;
  if ($[52] !== activeDrop || $[53] !== editingDropId || $[54] !== isApp || $[55] !== onCancelReplyQuote || $[56] !== wave) {
    t16 = !(isApp && editingDropId) && <div className="tw-mt-auto"><CreateDropWaveWrapper><PrivilegedDropCreator activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} onDropAddedToQueue={onCancelReplyQuote} wave={wave} dropId={null} fixedDropMode={DropMode.BOTH} /></CreateDropWaveWrapper></div>;
    $[52] = activeDrop;
    $[53] = editingDropId;
    $[54] = isApp;
    $[55] = onCancelReplyQuote;
    $[56] = wave;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  let t17;
  if ($[58] !== submissionExperience || $[59] !== wave) {
    t17 = submissionExperience === WaveSubmissionExperience.MEMES_LEGACY && <MobileMemesArtSubmissionBtn wave={wave} />;
    $[58] = submissionExperience;
    $[59] = wave;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  let t18;
  if ($[61] !== t15 || $[62] !== t16 || $[63] !== t17 || $[64] !== waveViewStyle) {
    t18 = <div ref={containerRef} className="tw-w-full tw-flex tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden lg:tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 scroll-shadow tw-flex-grow" style={waveViewStyle}>{t15}{t16}{t17}</div>;
    $[61] = t15;
    $[62] = t16;
    $[63] = t17;
    $[64] = waveViewStyle;
    $[65] = t18;
  } else {
    t18 = $[65];
  }
  let t19;
  if ($[66] !== t11 || $[67] !== t12 || $[68] !== t14 || $[69] !== t18) {
    t19 = <UnreadDividerProvider initialSerialNo={t11} key={t12}>{t14}{t18}</UnreadDividerProvider>;
    $[66] = t11;
    $[67] = t12;
    $[68] = t14;
    $[69] = t18;
    $[70] = t19;
  } else {
    t19 = $[70];
  }
  return t19;
};
export default MyStreamWaveChat;